class PreviewSelector extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this._name = "";
    this._number = "";
    this._textColor = "";
    this._borderColor = "";
    this._canvas = null;
    this._ctx = null;
  }

  static get observedAttributes() {
    return ["name", "number", "text-color", "border-color"];
  }

  get name() {
    return this._name;
  }

  set name(val) {
    this._name = val == null ? "" : String(val);
    if (this.isConnected) this._renderText();
    if (this.getAttribute("name") !== this._name)
      this.setAttribute("name", this._name);
  }

  get number() {
    return this._number;
  }

  set number(val) {
    this._number = val == null ? "" : String(val);
    if (this.isConnected) this._renderText();
    if (this.getAttribute("number") !== this._number)
      this.setAttribute("number", this._number);
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (name === "name") {
      this._name = newVal == null ? "" : String(newVal);
      this._renderText();
    } else if (name === "number") {
      this._number = newVal == null ? "" : String(newVal);
      this._renderText();
    } else if (name === "text-color") {
      this._textColor = newVal == null ? "" : String(newVal);
      this._renderText();
    } else if (name === "border-color") {
      this._borderColor = newVal == null ? "" : String(newVal);
      this._renderText();
    }
  }

  connectedCallback() {
    this._render();
    this._initCanvas();

    // Redibujar automáticamente cuando la fuente se cargue
    document.fonts.load('12px "LALIGAPlayers Regular"').then(() => {
      this._renderText();
    });
  }

  disconnectedCallback() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
  }

  _render() {
    this.shadowRoot.innerHTML = `
        <style>${this._getStyles()}</style>
        <canvas id="canvas-preview-selector"></canvas>
    `;
    this.style.display = "block";
    if (this.hasAttribute("name")) this._name = this.getAttribute("name") || "";
    if (this.hasAttribute("number"))
      this._number = this.getAttribute("number") || "";
  }

  _getStyles() {
    return `
        :host {
            display: block;
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 1000;
            pointer-events: none;
        }
        :host([inline]) {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 10;
            pointer-events: none;
        }
        #canvas-preview-selector {
            display: block;
            width: 100%;
            height: 100%;
        }
    `;
  }

  _initCanvas() {
    this._canvas = this.shadowRoot.getElementById("canvas-preview-selector");
    if (!this._canvas) return;
    this._ctx = this._canvas.getContext("2d");

    this._updateSize();

    this._resizeObserver = new ResizeObserver(() => this._updateSize());
    this._resizeObserver.observe(this);
  }

  _updateSize() {
    if (!this._canvas) return;
    const rect = this.getBoundingClientRect();
    const w = Math.max(1, Math.floor(rect.width));
    const h = Math.max(1, Math.floor(rect.height));
    const ratio = window.devicePixelRatio || 1;

    this._canvas.width = Math.floor(w * ratio);
    this._canvas.height = Math.floor(h * ratio);
    this._canvas.style.width = `${w}px`;
    this._canvas.style.height = `${h}px`;

    if (this._ctx && this._ctx.setTransform) {
      this._ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    }

    this._renderText();
  }

  _renderText() {
    if (!this._ctx || !this._canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const cw = this._canvas.width;
    const ch = this._canvas.height;

    this._ctx.clearRect(0, 0, cw, ch);
    const logicalWidth = cw / ratio;
    const logicalHeight = ch / ratio;

    this._ctx.save();

    // Nombre
    if (this._name) {
      const nameFontSize = Math.max(
        12,
        Math.round(Math.min(logicalWidth, logicalHeight) * 0.08),
      );

      this._ctx.font = `${nameFontSize}px 'LALIGAPlayers Regular', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial`;
      this._ctx.textAlign = "center";
      this._ctx.textBaseline = "middle";

      const text = this._name.toUpperCase();
      const baseRadius = logicalWidth * 0.2;
      const centerX = logicalWidth * 0.5;
      const centerY = logicalHeight * 0.38;

      // Medir ancho de cada letra
      const charWidths = text
        .split("")
        .map((c) => this._ctx.measureText(c).width);
      const totalWidth = charWidths.reduce((a, b) => a + b, 0);

      // Separación entre letras: 1 = espaciado natural de la fuente.
      // Se mide en la base de las letras (radio interior), que es donde se
      // juntan al curvar el texto hacia arriba.
      const letterSpacing = 1.03;
      const arcLength = totalWidth * letterSpacing;

      // Ángulo mínimo para nombres cortos y ángulo máximo de curvatura
      const minCurveAngle = 0.35;
      const maxCurveAngle = (60 * Math.PI) / 180;

      let radius = baseRadius;
      let arcCenterY = centerY;
      let innerRadius = Math.max(1, radius - nameFontSize / 2);
      let totalAngle = Math.max(minCurveAngle, arcLength / innerRadius);

      // Nombres largos: en vez de abrir más el ángulo, aumentar el radio para
      // que el arco sea más plano, y bajar el centro para que el punto más
      // alto del texto quede en la misma posición.
      if (totalAngle > maxCurveAngle) {
        totalAngle = maxCurveAngle;
        innerRadius = arcLength / maxCurveAngle;
        radius = innerRadius + nameFontSize / 2;
        arcCenterY = centerY + (radius - baseRadius);
      }

      const anglePerUnit = totalAngle / totalWidth;

      this._ctx.save();
      this._ctx.translate(centerX, arcCenterY);
      this._ctx.lineWidth = 2;
      this._ctx.strokeStyle = this._borderColor || "#fff";
      this._ctx.fillStyle = this._textColor ?? "rgba(0,0,0,0.7)";

      let angle = -totalAngle / 2; // empezar desde el medio

      for (let i = 0; i < text.length; i++) {
        const char = text[i];
        const cw = charWidths[i];
        const charAngle = cw * anglePerUnit;

        this._ctx.save();
        this._ctx.rotate(angle + charAngle / 2);
        this._ctx.translate(0, -radius);
        this._ctx.strokeText(char, 0, 0);
        this._ctx.fillText(char, 0, 0);
        this._ctx.restore();

        angle += charAngle;
      }

      this._ctx.restore();
    }

    // Número
    if (this._number) {
      this._renderNumberSvg(logicalWidth, logicalHeight);
    }

    this._ctx.restore();
  }

  _loadSvg(url) {
    return fetch(url)
      .then((response) => response.text())
      .then((svgText) => {
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgText, "image/svg+xml");
        return svgDoc.documentElement;
      });
  }

  _renderNumberSvg(logicalWidth, logicalHeight) {
    const digits = this._number.toString().split("").slice(0, 2);

    const svgPromises = digits.map((d) =>
      this._loadSvg(window.themeVariables.previewSvgs[d]),
    );

    Promise.all(svgPromises).then((svgElements) => {
      const desiredHeight = logicalHeight * 0.22;

      const svgDimensions = svgElements.map((svg) => ({
        width: parseFloat(svg.getAttribute("width")) || 50,
        height: parseFloat(svg.getAttribute("height")) || 100,
      }));

      const scales = svgDimensions.map((dim) => desiredHeight / dim.height);
      const scaledWidths = svgDimensions.map((dim, i) => dim.width * scales[i]);

      const spacing = digits.length > 1 ? 3 : 0;
      const totalWidth = scaledWidths.reduce((a, b) => a + b, 0) + spacing;
      let startX = (logicalWidth - totalWidth) / 2;
      const imgY = logicalHeight * 0.46 - desiredHeight / 2;

      digits.forEach((digit, index) => {
        const svg = svgElements[index];

        // 🔹 Modificar solo los fills y strokes específicos
        svg.querySelectorAll("[fill]").forEach((el) => {
          if ((el.getAttribute("fill") || "").toUpperCase() === "BLACK") {
            el.setAttribute("fill", this._textColor || "black");
          }
        });

        svg.querySelectorAll("[fill]").forEach((el) => {
          if ((el.getAttribute("fill") || "").toUpperCase() === "WHITE") {
            el.setAttribute("fill", this._borderColor || "white");
          }
        });

        const desiredWidth = scaledWidths[index];
        const imgX = startX;

        const img = new Image();
        const svgData = new XMLSerializer().serializeToString(svg);
        const svgBlob = new Blob([svgData], { type: "image/svg+xml" });
        const url = URL.createObjectURL(svgBlob);

        img.onload = () => {
          this._ctx.drawImage(img, imgX, imgY, desiredWidth, desiredHeight);
          URL.revokeObjectURL(url);
        };
        img.src = url;

        startX += desiredWidth + spacing;
      });
    });
  }
}

customElements.define("preview-selector", PreviewSelector);
